import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import { motion } from "framer-motion";
import { BsCheckCircleFill } from "react-icons/bs";
import { FaStar, FaGoogle } from "react-icons/fa";

const STEPS = [
  "Choose Module",
  "Select Certification",
  "Attend Quiz",
  "Google Review",
  "Download Certificate",
];

const GOOGLE_REVIEW_URL =
  "https://www.google.com/search?q=certvalue+google+reviews#lrd=0x3bae159cb9a01cdb:0x3112aea4314f37ce,3,,,";

const GoogleReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState(
    location.state?.steps || [true, true, true, false, false],
  );
  const [hasClickedReview, setHasClickedReview] = useState(false);
  const [timer, setTimer] = useState(0);
  const [showCertificateButton, setShowCertificateButton] = useState(false);
  const { certification, score, totalQuestions } = location.state || {};

  useEffect(() => {
    const reviewCompleted = sessionStorage.getItem(
      `review_completed_${certification}`,
    );
    if (reviewCompleted === "true") {
      setShowCertificateButton(true);
      setSteps([true, true, true, true, false]);
    }
  }, [certification]);

  useEffect(() => {
    let interval;
    if (hasClickedReview && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && hasClickedReview) {
      setShowCertificateButton(true);
      setSteps([true, true, true, true, false]);
      sessionStorage.setItem(`review_completed_${certification}`, "true");
    }
    return () => clearInterval(interval);
  }, [hasClickedReview, timer, certification]);

  const handleReviewClick = () => {
    window.open(GOOGLE_REVIEW_URL, "_blank");
    setHasClickedReview(true);
    setTimer(40);
  };

  const handleCertificateDownload = () => {
    navigate("/user-certificate", {
      state: {
        certification,
        score,
        totalQuestions,
        steps: [true, true, true, true, true],
      },
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = totalQuestions
    ? Math.round((score / totalQuestions) * 100)
    : 0;

  const breadcrumbVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-100/90 via-white/70 to-red-100/90">
      <Navbar />
      <div className="container mx-auto px-4 py-8 sm:py-10">
        {/* STEPPER */}
        <div className="flex justify-center mb-8 px-2">
          <div className="flex items-center bg-white/80 backdrop-blur-md shadow-lg rounded-full p-2 flex-wrap gap-2">
            {STEPS.map((stepLabel, index) => (
              <motion.div
                key={index}
                className="flex items-center"
                initial="initial"
                animate="animate"
                variants={breadcrumbVariants}
                transition={{ duration: 0.4, delay: index * 0.15 }}
              >
                {/* STEP PILL */}
                <div
                  className={`flex items-center px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all duration-300
          ${
            steps[index]
              ? "bg-green-500 text-white shadow-md"
              : index === 0
                ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md"
                : "bg-gray-100 text-gray-600"
          }`}
                >
                  {/* STEP CIRCLE */}
                  <span
                    className={`mr-2 flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold transition
            ${
              steps[index]
                ? "bg-white text-green-500"
                : index === 0
                  ? "bg-white text-red-600"
                  : "bg-gray-300 text-gray-700"
            }`}
                  >
                    {steps[index] ? <BsCheckCircleFill /> : index + 1}
                  </span>

                  {/* LABEL */}
                  <span className="whitespace-nowrap">{stepLabel}</span>
                </div>

                {/* CONNECTOR LINE */}
                {index < STEPS.length - 1 && (
                  <div className="hidden md:block w-6 h-[2px] bg-gray-300 mx-1" />
                )}
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto bg-white rounded-2xl  p-8 sm:p-10 border border-yellow-500/50 shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
        >
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="flex items-center justify-center w-24 h-24 mx-auto bg-green-600/10 rounded-full mb-4"
            >
              <BsCheckCircleFill className="text-green-500 text-5xl" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
              Congratulations!
            </h1>
            <p className="text-lg text-gray-500 mt-2">
              You've successfully passed the quiz for
            </p>
            <p className="text-xl font-semibold text-blue-700 mt-2 tracking-wide">
              {certification?.toUpperCase()}
            </p>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-blue-100/60 border border-yellow-500/20 rounded-xl p-6 mb-6">
            <div className="flex flex-col sm:flex-row justify-between gap-8 items-center">
              <div>
                <p className="text-gray-600 text-sm uppercase tracking-wider">
                  Your Score
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {score}
                  <span className="text-gray-400 text-lg">
                    {" "}
                    / {totalQuestions}
                  </span>
                </p>
              </div>

              <div className="w-full">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Performance</span>
                  <span className="text-yellow-700">{progress}%</span>
                </div>

                <div className="w-full h-2 bg-gray-700 rounded-full">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-yellow-600 to-yellow-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="text-center ">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                One Last Step ⭐
              </h2>
              <p className="text-gray-600 mb-6">
                Share your experience with a Google review to unlock your
                certificate. Your feedback helps us serve you better!
              </p>
            </div>

            {!showCertificateButton ? (
              <div className="space-y-4">
                <button
                  onClick={handleReviewClick}
                  disabled={hasClickedReview}
                  className={`w-full flex items-center justify-center gap-3 py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 ${
                    hasClickedReview
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-blue-800 hover:scale-[1.02] text-white shadow-lg"
                  }`}
                >
                  <FaGoogle className="text-xl " />
                  {hasClickedReview
                    ? "Review Window Opened"
                    : "Write a Google Review"}
                </button>

                {hasClickedReview && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <FaStar className="text-yellow-500" />
                      <p className="font-semibold text-gray-700">
                        Certificate will be unlocked in:
                      </p>
                    </div>
                    <p className="text-3xl font-bold text-center text-blue-700">
                      {formatTime(timer)}
                    </p>
                    <p className="text-sm text-gray-600 text-center mt-2">
                      Complete your review and come back to this page
                    </p>
                  </motion.div>
                )}

                <p className="text-sm text-gray-500 text-center mt-4">
                  💡 A 5-star rating makes us very happy!
                </p>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-700 font-semibold text-center flex items-center justify-center gap-2">
                    <BsCheckCircleFill className="text-green-600" />
                    Thank you for your review!
                  </p>
                </div>

                <button
                  onClick={handleCertificateDownload}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-4 px-6 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  📜 Download Certificate
                </button>
              </motion.div>
            )}
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Your certificate is emailed to your registered email within 2–3
              hours after validation.{" "}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GoogleReview;
